import { useEffect, useId, useRef, useState } from 'react';

import { CheckIcon } from '../../icons/CheckIcon';
import { DiscountCode } from '../DiscountCode';
import {
  Content,
  Indicator,
  IndicatorWrapper,
  Trigger,
  Wrapper,
} from './DiscountCodeDisclosure.styles';
import type { DiscountCodeDisclosureProps } from './DiscountCodeDisclosure.types';

const DEFAULT_PROMPT = 'Add voucher or promotion code';

export function DiscountCodeDisclosure({
  promoCode,
  voucherCode,
  defaultOpen,
  labels,
  className,
}: DiscountCodeDisclosureProps) {
  const hasAppliedCode = Boolean(promoCode?.applied?.length || voucherCode?.applied?.length);
  const [open, setOpen] = useState(defaultOpen ?? hasAppliedCode);
  const hadAppliedCode = useRef(hasAppliedCode);
  const contentId = useId();

  useEffect(() => {
    if (defaultOpen === undefined && hasAppliedCode && !hadAppliedCode.current) setOpen(true);
    hadAppliedCode.current = hasAppliedCode;
  }, [defaultOpen, hasAppliedCode]);

  if (!promoCode && !voucherCode) return null;

  return (
    <Wrapper className={className} data-testid="discount-code-disclosure">
      <Trigger
        type="button"
        aria-expanded={open}
        aria-controls={open ? contentId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {labels?.prompt ?? DEFAULT_PROMPT}
        <IndicatorWrapper aria-hidden="true">
          <Indicator data-checked={open}>{open && <CheckIcon size={16} />}</Indicator>
        </IndicatorWrapper>
      </Trigger>
      {open && (
        <Content id={contentId}>
          {promoCode && <DiscountCode {...promoCode} />}
          {voucherCode && <DiscountCode {...voucherCode} />}
        </Content>
      )}
    </Wrapper>
  );
}
